import {
  AccessToken,
  IDToken,
  RefreshToken,
  SessionObject,
  UserClaims,
} from '@okta/okta-auth-js';
import { Reducer } from 'react';

export type AuthProps = {
  isAuthenticated: boolean;
  accessToken?: AccessToken;
  idToken?: IDToken;
  refreshToken?: RefreshToken;
  user?: UserClaims;
  initialState: any;
  session: SessionObject | undefined;
};

export type ActionProps = { type: string } & AuthProps;

export const AuthState = {
  isAuthenticated: false,
  accessToken: undefined,
  idToken: undefined,
  refreshToken: undefined,
  user: undefined,
  initialState: undefined,
  session: undefined,
};

export const ActionTypes = {
  AUTHENTICATION_SUCCESS: 'AUTHENTICATION_SUCCESS',
  UPDATE_USER: 'UPDATE_USER',
  UPDATE_STATE: 'UPDATE_STATE',
  UPDATE_SESSION: 'UPDATE_SESSION',
  AUTHENTICATION_LOGOUT: 'AUTHENTICATION_LOGOUT',
};

const getUpdateParams = (state: AuthProps, queryParams) => {
  const { initialState } = state;
  if (!initialState) {
    return queryParams;
  } else if (Object.keys(initialState).length > 0) {
    return initialState;
  } else {
    return {};
  }
};

const shallowEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;

  // If either is null or undefined (but not both), they are not equal
  if (obj1 == null || obj2 == null) return false;

  // Both must be objects (and not arrays or functions if you want stricter checks)
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (let key of keys1) {
    if (obj1[key] !== obj2[key]) return false;
  }

  return true;
};

const AuthReducer: Reducer<AuthProps, ActionProps> = (state, action) => {
  switch (action.type) {
    case ActionTypes.AUTHENTICATION_SUCCESS: {
      return {
        ...state,
        isAuthenticated: action.isAuthenticated,
        accessToken: action.accessToken,
        idToken: action.idToken,
        refreshToken: action.refreshToken,
        user: action.user,
        initialState: getUpdateParams(state, action.initialState),
      };
    }
    case ActionTypes.UPDATE_USER: {
      return {
        ...state,
        user: action.user,
      };
    }
    case ActionTypes.UPDATE_STATE: {
      return {
        ...state,
        initialState: action.initialState,
      };
    }
    case ActionTypes.UPDATE_SESSION: {
      if (shallowEqual(state.session, action.session)) return state;
      return {
        ...state,
        session: action.session,
      };
    }
    case ActionTypes.AUTHENTICATION_LOGOUT: {
      return {
        ...state,
        isAuthenticated: false,
        accessToken: undefined,
        idToken: undefined,
        user: undefined,
      };
    }
    default:
      return state;
  }
};

export default AuthReducer;
